import type { Accessor, Setter, Signal } from 'solid-js';
import type { SetStoreFunction, Store } from 'solid-js/store';
import { makePersisted, PersistenceOptions } from '@solid-primitives/storage';
import {
  isBoolean,
  isNaN,
  isNull,
  isNumber,
  isString,
  merge,
  toNumber,
} from 'lodash';

// These types are not re-exported from the main @solid-primitives/storage entrypoint,
// and the deep import path (dist/persisted) is not in the package's exports map.
// Inlined from @solid-primitives/storage/dist/persisted.d.ts.
type SignalInput = Signal<any> | [Store<any>, SetStoreFunction<any>];
type SignalType<S extends SignalInput> =
  S extends Signal<infer T>
    ? T
    : S extends [Store<infer T>, SetStoreFunction<infer T>]
      ? T
      : never;
type PersistedState<S extends SignalInput> =
  S extends Signal<infer T>
    ? [get: Accessor<T>, set: Setter<T>, init: Promise<string> | string | null]
    : S extends [Store<infer T>, SetStoreFunction<infer T>]
      ? [
          get: Store<T>,
          set: SetStoreFunction<T>,
          init: Promise<string> | string | null,
        ]
      : never;

/**
 * This function ensures that defaults are retained when local storage
 * is parsed. The solid-primitives makePersisted function completely replaces
 * the initial value of the signal with the value from local storage.
 * @param signal
 * @param options
 * @returns
 */
export function makeMergedPersisted<S extends SignalInput>(
  signal: S,
  options?: PersistenceOptions<SignalType<S>, undefined> & {
    validate?(value: SignalType<S>): boolean;
  }
): PersistedState<S> {
  const getter = Array.isArray(signal) ? signal[0] : signal;

  // Handle both signals (where getter is a function) and stores (where getter is an object)
  const defaults: SignalType<S> =
    typeof getter === 'function' ? getter() : getter;

  const parseValue = (value: string): SignalType<S> => {
    try {
      const parsed = JSON.parse(value) as unknown;

      // If both parsed and defaults are arrays, return parsed as-is
      if (Array.isArray(parsed) && Array.isArray(defaults)) {
        return parsed as SignalType<S>;
      }

      // If the parsed value is an object (but not array), merge with defaults
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed)
      ) {
        return merge({}, defaults, parsed) as SignalType<S>;
      }

      // Primitive fallback
      if (
        isString(parsed) ||
        isNumber(parsed) ||
        isBoolean(parsed) ||
        isNull(parsed)
      ) {
        return parsed as SignalType<S>;
      }
    } catch {
      // Fall through to handle unstructured strings
    }

    // Try to recover from common primitives

    if (value === 'true') return true as SignalType<S>;

    if (value === 'false') return false as SignalType<S>;

    if (value === 'null') return null as SignalType<S>;

    if (value === 'undefined') return undefined as SignalType<S>;

    // Try to parse as number
    const numValue = toNumber(value);
    if (isNumber(numValue) && !isNaN(numValue)) {
      return numValue as SignalType<S>;
    }

    // If all else fails, treat as string or return defaults
    if (isString(defaults)) {
      return value as SignalType<S>;
    }

    return defaults;
  };

  return makePersisted(signal, {
    ...options,
    deserialize: (value) => {
      const parsed = parseValue(value);

      // If no validator or validator passes, return parsed
      if (!options?.validate || options.validate(parsed)) return parsed;

      // Validator failed
      return defaults;
    },
  });
}
